'use client'

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

/**
 * In-code mock of the audit "blind spots" finding tabs — mirrors the reference
 * layout's tabbed report without shipping a screenshot. Illustrative findings.
 */

type Finding = {
  key: string
  tab: string
  headline: string
  points: string[]
}

const FINDINGS: Finding[] = [
  {
    key: 'wasted',
    tab: 'Wasted spend',
    headline: '$3,180 / mo going to converting nobody',
    points: [
      '7 ad sets with 0 conversions in 14 days, still funded',
      '2 search campaigns paying for branded terms you already rank #1 for',
      'Broad-match terms drained 11% of search budget on irrelevant queries',
    ],
  },
  {
    key: 'fatigue',
    tab: 'Creative fatigue',
    headline: '3 winners are now your biggest leak',
    points: [
      'Frequency above 4.5 on your 3 highest-spend creatives',
      'CTR down 36% over 21 days while CPM rose 18%',
      'No new creative shipped to the top audience in 5 weeks',
    ],
  },
  {
    key: 'funnel',
    tab: 'Funnel leakage',
    headline: 'Clicks are fine — the landing page isn’t',
    points: [
      'Add-to-cart → checkout drop-off 22% above category benchmark',
      'Mobile LCP 4.1s on the top paid landing page',
      'Top campaign sends traffic to a 404-redirected URL',
    ],
  },
  {
    key: 'attribution',
    tab: 'Attribution gaps',
    headline: 'You’re crediting the wrong channel',
    points: [
      'GA4 and Meta disagree on 38% of conversions',
      'Post-purchase upsells counted as new acquisition',
      'Branded search inflating blended ROAS by ~0.6x',
    ],
  },
]

export function BlindSpotsTabs({ className }: { className?: string }) {
  return (
    <Tabs defaultValue={FINDINGS[0].key} className={className}>
      <TabsList className="flex h-auto w-full flex-wrap justify-start gap-2 bg-transparent p-0">
        {FINDINGS.map(f => (
          <TabsTrigger
            key={f.key}
            value={f.key}
            className="rounded-full border border-border bg-card px-4 py-2 text-sm data-[state=active]:border-primary data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
          >
            {f.tab}
          </TabsTrigger>
        ))}
      </TabsList>
      {FINDINGS.map(f => (
        <TabsContent key={f.key} value={f.key} className="mt-6">
          <div className="rounded-2xl border border-border bg-card p-6 text-left shadow-sm">
            <h3 className="text-lg font-semibold">{f.headline}</h3>
            <ul className="mt-4 space-y-3">
              {f.points.map(p => (
                <li key={p} className="flex gap-3 text-sm leading-6">
                  <span className="mt-2 size-1.5 shrink-0 rounded-full bg-primary" />
                  <span className="text-muted-foreground">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        </TabsContent>
      ))}
    </Tabs>
  )
}
